// ======================================================================
// 📁 File: RawMaterialDrawer.jsx
// 📝 Purpose: Create / Edit Raw Material
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";
import AttributeSelector from "./AttributeSelector";
import AttributeMappingModal from "./AttributeMappingModal";

export default function RawMaterialDrawer({
  open,
  mode = "create",
  record = null,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    base_uom_id: "",
    issue_uom_id: "",
    sku_type: "ENG",
    // --------------------------------------------------------------
    // ADDED: Tax classification field
    // --------------------------------------------------------------
    tax_classification_id: "",

    description: "",
    is_active: 1,
    status_id: 1,
  });

  const [uoms, setUoms] = useState([]);

  // --------------------------------------------------------------
  // ADDED: State to hold tax classification list
  // --------------------------------------------------------------
  const [taxList, setTaxList] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [attributes, setAttributes] = useState([]);
  //const [engSku, setEngSku] = useState("");
  const [showMapping, setShowMapping] = useState(false);
  const [attrRefreshKey, setAttrRefreshKey] = useState(0);

  // --------------------------------------------------------------
  // CHANGED: Load both UOM and Tax Classification masters together
  // --------------------------------------------------------------
  useEffect(() => {
    async function loadMasters() {
      try {
        // Load UOMs
        const uomRes = await axiosClient.get("/api/ppe/masters/uoms");
        setUoms(uomRes.data?.data || []);

        // --------------------------------------------------------------
        // ADDED: Load Tax Classifications
        // --------------------------------------------------------------
        const taxRes = await axiosClient.get(
          "/api/ppe/masters/tax-classifications"
        );
        setTaxList(taxRes.data?.data || []);
      } catch (err) {
        console.error("Failed to load masters", err);
      }
    }

    if (open) {
      loadMasters();
    }
  }, [open]);

  // --------------------------------------------------------------
  // CHANGED: Added tax_classification_id handling in edit mode
  // --------------------------------------------------------------
  useEffect(() => {

    const loadData = async () => {

      // ============================================================
      // EDIT MODE   if (mode === "edit" && record)
      // ============================================================
      if (mode === "edit" && record?.id) {

        setForm({
          code: record.code || "",
          name: record.name || "",
          base_uom_id: record.base_uom_id || "",
          issue_uom_id: record.issue_uom_id || "",
          sku_type: record.sku_type || "ENG",

          // Tax
          tax_classification_id: record.tax_classification_id || "",

          description: record.description || "",
          is_active:
            record.is_active === false || record.is_active === 0 ? 0 : 1,
          status_id: record.status_id || 1,
        });

        // ============================================================
        // 🔥 LOAD ATTRIBUTE VALUES (IMPORTANT)
        // ============================================================
        try {
          const res = await axiosClient.get(
            `/api/ppe/sku-attributes/material/RAW_MATERIAL/${record.id}`
          );

          if (res.data?.success) {
            setAttributes(res.data.data || []);
          } else {
            setAttributes([]);
          }

        } catch (err) {
          console.error("Failed to load material attributes", err);
          setAttributes([]);
        }

      }

      // ============================================================
      // CREATE MODE
      // ============================================================
      else {

        setForm({
          code: "",
          name: "",
          base_uom_id: "",
          issue_uom_id: "",
          sku_type: "ENG",
          tax_classification_id: "",

          description: "",
          is_active: 1,
          status_id: 1,
        });

        // 🔥 RESET ATTRIBUTES
        setAttributes([]);
      }
    };

    if (open) {
      loadData();
    }

  }, [mode, record, open]);

  // --------------------------------------------------------------
// 🔥 CLEAR ATTRIBUTES WHEN SWITCHING SKU TYPE
// --------------------------------------------------------------
  useEffect(() => {
    if (form.sku_type === "ENG") {
      setAttributes([]);
    }
  }, [form.sku_type]);

  if (!open) return null;

  const onChange = (field, value) => {
    setError(null);

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onSubmit = async () => {
    setError(null);

    // --------------------------------------------------------------
    // CHANGED: Validation to include tax classification
    // --------------------------------------------------------------
    if (
      !form.code ||
      !form.name ||
      !form.base_uom_id ||
      !form.tax_classification_id ||
      !form.sku_type
    ) {
      setError(
        "Code, Name, Base UOM and Tax Classification are mandatory"
      );
      return;
    }

    // --------------------------------------------------------------
    // 🔥 NEW: Manufacturer SKU must have Internal SKU selected
    // --------------------------------------------------------------
    if (form.sku_type === "MANUFACTURER") {
      const hasEngSku = (attributes || []).some(
        (a) => a.value && a.value !== ""
      );

      if (!hasEngSku) {
        setError("Please select Internal (ENG) SKU below");

        // 🔥 scroll to attribute section
        setTimeout(() => {
          document.querySelector(".attr-container")?.scrollIntoView({
            behavior: "smooth"
          });
        }, 100);

        return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        code: form.code,
        name: form.name,
        sku_type: form.sku_type,
        base_uom_id: parseInt(form.base_uom_id),
        issue_uom_id: parseInt(
          form.issue_uom_id || form.base_uom_id
        ),

        // --------------------------------------------------------------
        // ADDED: Send tax classification to backend
        // --------------------------------------------------------------
        tax_classification_id: parseInt(
          form.tax_classification_id
        ),

        description: form.description,
        is_active: form.is_active,
        attributes: attributes || []
      };

      if (mode === "create") {
        await axiosClient.post(
          "/api/ppe/masters/raw-materials",
          payload
        );
      }

      if (mode === "edit" && record?.id) {
        await axiosClient.put(
          `/api/ppe/masters/raw-materials/${record.id}`,
          payload
        );
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 1200,
        height: "100vh",
        background: "#fff",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: 16,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h6 style={{ margin: 0 }}>
          {mode === "create"
            ? "Create Raw Material"
            : "Edit Raw Material"}
        </h6>
        <button className="btn btn-sm btn-light" onClick={onClose}>
          ✕
        </button>
      </div>

      <div style={{ padding: 16, flex: 1, overflowY: "auto" }}>
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="mb-3">
          <label className="form-label">Code *</label>
          <input
            className="form-control"
            value={form.code}
            disabled={mode === "edit"}
            onChange={(e) => onChange("code", e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Name *</label>
          <input
            className="form-control"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
          />
        </div>
        {/* ✅ SKU TYPE */}
        <div className="mb-3">
          <label className="form-label">SKU Type *</label>
          <select
            className="form-select"
            value={form.sku_type}
            onChange={(e) => onChange("sku_type", e.target.value)}
          >
            <option value="ENG">ENG (Internal SKU)</option>
            <option value="MANUFACTURER">Manufacturer SKU</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Base UOM *</label>
          <select
            className="form-select"
            value={form.base_uom_id}
            onChange={(e) =>
              onChange("base_uom_id", e.target.value)
            }
          >
            <option value="">-- Select Base UOM --</option>
            {uoms.map((u) => (
              <option key={u.id} value={u.id}>
                {u.uom_code} - {u.uom_name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Issue UOM</label>
          <select
            className="form-select"
            value={form.issue_uom_id}
            onChange={(e) =>
              onChange("issue_uom_id", e.target.value)
            }
          >
            <option value="">-- Select Issue UOM --</option>
            {uoms.map((u) => (
              <option key={u.id} value={u.id}>
                {u.uom_code} - {u.uom_name}
              </option>
            ))}
          </select>
        </div>

        {/* --------------------------------------------------------------
            ADDED: Tax Classification Dropdown
        -------------------------------------------------------------- */}
        <div className="mb-3">
          <label className="form-label">
            Tax Classification (HSN/SAC) *
          </label>

          <select
            className="form-select"
            value={form.tax_classification_id}
            onChange={(e) =>
              onChange("tax_classification_id", e.target.value)
            }
          >
            <option value="">-- Select Tax Classification --</option>

            {taxList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.tax_system} - {t.tax_code} ({t.description})
              </option>
            ))}
          </select>
        </div>
        
        
        {/* -------------------------------------------------------------- */}
        
        {/* 🔥 ADD BUTTON HERE */}
        <div className="mb-3">
          <button
            className="btn btn-outline-primary"
            disabled={!record?.id}
            onClick={() => setShowMapping(true)}
          >
            Map Attributes
          </button>

          {!record?.id && (
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Save material first to map attributes
            </div>
          )}
        </div>
        
        {/* 🔥 ADD HERE */}
          {true && (
              <div className="mb-3">
                <AttributeSelector
                  entityType="RAW_MATERIAL"
                  entityId={record?.id}
                  value={attributes}
                  refreshKey={form.sku_type}
                  onChange={(val) => {
                    setAttributes(val);
                  }}
                />
              </div>
            )}    
        
        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            value={form.description}
            onChange={(e) =>
              onChange("description", e.target.value)
            }
          />
        </div>

        <div className="mb-3">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) =>
              onChange("is_active", e.target.checked ? 1 : 0)
            }
          />
          <label className="ms-2">Active</label>
        </div>
      </div>

      <div
        style={{
          padding: 16,
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button className="btn btn-outline-secondary" onClick={onClose}>
          Cancel
        </button>

        <button
          className="btn btn-primary"
          disabled={saving}
          onClick={onSubmit}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      
      {/* 🔥 ADD MODAL HERE */}
      <AttributeMappingModal
        open={showMapping}
        entityId={record?.id}
        entityType="RAW_MATERIAL"
        onClose={() => setShowMapping(false)}
        onSaved={() => {
          setShowMapping(false);
           setAttrRefreshKey(prev => prev + 1); // 🔥 trigger reload
        }}
      />
    </div>
  );
}
